import {
    Box,
    Typography,
    Chip,
    Paper,
    Collapse,
    IconButton,
    CircularProgress
} from "@mui/material";
import { useState, useEffect } from "react";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import AddIcon from '@mui/icons-material/Add';
import { getTaskSuggestions } from "../../services/TaskService";
import {RTL} from "../../cache/CacheRtl";
import { t } from "../dashboard/coach/coachTokens";

const TaskSuggestionsPanel = ({ onSelect }) => {
    const [expanded, setExpanded] = useState(true);
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadSuggestions();
    }, []);

    const loadSuggestions = async () => {
        try {
            const response = await getTaskSuggestions();
            setSuggestions(response.data || []);
        } catch (error) {
            console.error("Error loading suggestions:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        onSelect(suggestion);
    };

    const clientSuggestions = suggestions.filter(s => s.suggestionType === 'CLIENT' || s.suggestionType === 'PROGRAM');
    const templateSuggestions = suggestions.filter(s => s.suggestionType === 'TEMPLATE');

    if (loading) {
        return (
            <Box sx={{
                display: "flex",
                justifyContent: "center",
                padding: "20px",
                backgroundColor: t.white
            }}>
                <CircularProgress size={24} sx={{ color: t.green }} />
            </Box>
        );
    }

    if (suggestions.length === 0) {
        return null;
    }

    return (
        <Paper sx={{
            borderRadius: t.rs,
            border: `1px solid ${t.border}`,
            boxShadow: t.sh,
            overflow: "hidden"
        }}>
            {/* Header */}
            <Box
                onClick={() => setExpanded(!expanded)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 16px",
                    backgroundColor: t.amberLight,
                    cursor: "pointer",
                    "&:hover": {
                        backgroundColor: "#ffeecc"
                    }
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LightbulbIcon sx={{ color: t.amber, fontSize: "20px" }} />
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: t.amberText }}>
                        הצעות למשימות
                    </Typography>

                    <RTL>
                        <Chip
                            label={suggestions.length}
                            size="small"
                            sx={{
                                height: "20px",
                                fontSize: "11px",
                                backgroundColor: "#ffeecc",
                                color: t.amberText
                            }}
                        />
                    </RTL>

                </Box>
                <IconButton size="small">
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </Box>


            <Collapse in={expanded}>
                <Box sx={{ padding: "16px" }}>
                    {/* Client-based suggestions */}
                    {clientSuggestions.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{
                                fontSize: "12px",
                                fontWeight: 600,
                                color: t.t2,
                                mb: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5
                            }}>
                                <PersonIcon sx={{ fontSize: "14px" }} />
                                מבוסס לקוחות
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {clientSuggestions.map((suggestion, index) => (
                                    <RTL>
                                        <Chip
                                            key={`client-${index}`}
                                            label={
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                                    <span>{suggestion.title}</span>
                                                    {suggestion.suggestionType === 'PROGRAM' && (
                                                        <EventIcon sx={{ fontSize: "14px", color: t.redText }} />
                                                    )}
                                                </Box>
                                            }
                                            onClick={() => handleSuggestionClick(suggestion)}
                                            icon={<AddIcon sx={{ fontSize: "16px" }} />}
                                            sx={{
                                                backgroundColor: suggestion.suggestionType === 'PROGRAM' ? t.redLight : t.blueLight,
                                                color: suggestion.suggestionType === 'PROGRAM' ? t.redText : t.blueText,
                                                fontSize: "12px",
                                                cursor: "pointer",
                                                "&:hover": {
                                                    backgroundColor: suggestion.suggestionType === 'PROGRAM' ? '#ffd9d9' : '#dde6ff'
                                                },
                                                "& .MuiChip-icon": {
                                                    color: "inherit"
                                                }
                                            }}
                                        />
                                    </RTL>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Quick templates */}
                    {templateSuggestions.length > 0 && (
                        <Box>
                            <Typography sx={{
                                fontSize: "12px",
                                fontWeight: 600,
                                color: t.t2,
                                mb: 1
                            }}>
                                תבניות מהירות
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                {templateSuggestions.map((suggestion, index) => (
                                    <RTL>
                                    <Chip
                                        key={`template-${index}`}
                                        label={suggestion.title}
                                        onClick={() => handleSuggestionClick(suggestion)}
                                        icon={<AddIcon sx={{ fontSize: "16px" }} />}
                                        variant="outlined"
                                        sx={{
                                            borderColor: t.border,
                                            color: t.t2,
                                            fontSize: "12px",
                                            cursor: "pointer",
                                            "&:hover": {
                                                backgroundColor: t.bg,
                                                borderColor: t.green
                                            },
                                            "& .MuiChip-icon": {
                                                color: t.t3
                                            }
                                        }}
                                    />
                                    </RTL>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Collapse>
        </Paper>
    );

};

export default TaskSuggestionsPanel;
